import { Container, Graphics, Rectangle } from 'pixi.js';
import { COLORS, DESIGN_W } from '../app/theme';
import { viewport } from '../app/viewport';
import { ModalCard } from './ModalCard';
import { Button, makeText, avatarSprite, getUiIcon } from './widgets';
import { ScrollList } from './ScrollList';
import type { Atlas } from '../assets/AtlasLoader';
import { toPersianDigits } from '../util/persian';

/** How many avatar frames the profile atlas ships (avatar_sp_0 … avatar_sp_63). */
const AVATAR_COUNT = 64;
const COLS = 5;

/**
 * A touch-friendly avatar picker modal (replaces the old 0..63 Slider, which gave ~9px per index and
 * was unusable). Shows a big live preview + a scrollable grid of tappable thumbnails with the selected
 * one ringed in gold. «انتخاب» confirms, «بازگشت» / scrim-tap cancels. Used by Profile and Register.
 */
export class AvatarPickerModal extends ModalCard {
  private index: number;
  private readonly atlas: Atlas;
  private readonly preview = new Container();
  private readonly previewLabel;
  private readonly grid: ScrollList;

  constructor(atlas: Atlas, current: number, onConfirm: (index: number) => void, onCancel: () => void) {
    // Near-fullscreen card so avatars are big and easy to browse (inset 12px each side via ModalCard).
    super({ title: 'تغییر آواتار', width: DESIGN_W - 24, height: Math.max(700, viewport.h - 24), onDismiss: onCancel });
    this.atlas = atlas;
    this.index = current;
    const w = this.cardWidth;
    const PREVIEW = 180;
    let y = this.contentTop + 8;

    // Big live preview.
    this.preview.position.set(w / 2 - PREVIEW / 2, y);
    this.content.addChild(this.preview);
    y += PREVIEW + 12;

    this.previewLabel = makeText('', { size: 20, fill: COLORS.textDim, align: 'center' });
    this.previewLabel.anchor.set(0.5, 0);
    this.previewLabel.position.set(w / 2, y);
    this.content.addChild(this.previewLabel);
    y += 34;

    // Scrollable thumbnail grid — fills the middle of the card (taller card → more rows visible).
    const listW = w - 40;
    const BTN_BLOCK = 64 + 14 + 60; // confirm + gap + back
    const gridH = Math.max(220, this.cardHeight - y - BTN_BLOCK - 24);
    this.grid = new ScrollList(listW, gridH);
    this.grid.position.set(20, y);
    this.content.addChild(this.grid);
    y += gridH + 16;

    const confirm = new Button('انتخاب', () => onConfirm(this.index), { width: w - 40, height: 64, fontSize: 24, icon: getUiIcon('save') });
    confirm.position.set(20, y);
    this.content.addChild(confirm);
    y += 78;
    const back = new Button('بازگشت', onCancel, {
      width: w - 40,
      height: 60,
      fontSize: 22,
      fill: COLORS.panel,
      textColor: COLORS.text,
    });
    back.position.set(20, y);
    this.content.addChild(back);

    this.refreshPreview();
    this.refreshGrid();
  }

  private refreshPreview(): void {
    this.preview.removeChildren().forEach((c) => c.destroy({ children: true }));
    this.preview.addChild(avatarSprite(this.atlas, this.index, 180));
    this.previewLabel.text = `آواتار ${toPersianDigits(this.index)}`;
  }

  private refreshGrid(): void {
    const listW = this.cardWidth - 40;
    const cell = Math.floor((listW - 14) / COLS);
    const thumb = cell - 12;
    const rows: Container[] = [];
    for (let start = 0; start < AVATAR_COUNT; start += COLS) {
      const row = new Container();
      for (let c = 0; c < COLS && start + c < AVATAR_COUNT; c++) {
        const i = start + c;
        const slot = new Container();
        slot.position.set(c * cell, 0);
        const av = avatarSprite(this.atlas, i, thumb);
        av.position.set((cell - thumb) / 2, 0);
        slot.addChild(av);
        if (i === this.index) {
          const ring = new Graphics()
            .circle(cell / 2, thumb / 2, thumb / 2 + 3)
            .stroke({ width: 4, color: COLORS.accent });
          slot.addChild(ring);
        }
        slot.eventMode = 'static';
        slot.cursor = 'pointer';
        slot.hitArea = new Rectangle(0, 0, cell, thumb);
        slot.on('pointertap', () => this.select(i));
        row.addChild(slot);
      }
      rows.push(row);
    }
    this.grid.setItems(rows);
  }

  private select(i: number): void {
    if (i === this.index) return;
    this.index = i;
    this.refreshPreview();
    this.refreshGrid();
  }
}
